/**
 * Slideshow Custom Element with Flickity Integration
 */

if (!customElements.get("slide-show")) {
  class SlideShow extends HTMLElement {
    static #flickityReady;
    static #ensureFlickity() {
      if (window.Flickity) return Promise.resolve();
      if (!this.#flickityReady) {
        this.#flickityReady = new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://unpkg.com/flickity@2/dist/flickity.pkgd.min.js";
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        }).then(() => {
          if (!document.querySelector('link[href*="flickity.min.css"]')) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = "https://unpkg.com/flickity@2/dist/flickity.min.css";
            document.head.appendChild(link);
          }
        });
      }
      return this.#flickityReady;
    }

    static get observedAttributes() {
      return ["data-defer-visible", "data-initial-height"];
    }

    constructor() {
      super();
      this.flickity = null;
      this.isInitialized = false;
      this.originalHTML = null; // Store original HTML for mobile/desktop switching

      this.#abort = new AbortController();
      this.#ro = null;
      this.#mo = null;
      this.#io = null;
      this.#resizeScheduled = false;
      this.#pendingImgCount = 0;
      this.#initialMinHeightSet = false;
    }

    connectedCallback() {
      this.#bootstrap().catch((e) => console.error("[SlideShow]", e));
    }

    disconnectedCallback() {
      this.#cleanup();
    }

    attributeChangedCallback() {
    }

    async #bootstrap() {
      await this.#waitDOMReady();

      // Store original HTML before any modifications
      if (!this.originalHTML) {
        this.originalHTML = this.innerHTML;
      }

      const deferVisible =
        (this.getAttribute("data-defer-visible") ?? "true") !== "false";

      if (deferVisible && "IntersectionObserver" in window) {
        await new Promise((resolve) => {
          this.#io = new IntersectionObserver(
            (entries) => {
              if (entries.some((e) => e.isIntersecting)) {
                this.#io.disconnect();
                this.#io = null;
                resolve();
              }
            },
            { rootMargin: "200px", threshold: 0.01 }
          );
          this.#io.observe(this);
        });
      }

      await SlideShow.#ensureFlickity();

      const initialH = parseInt(this.getAttribute("data-initial-height") || "", 10);
      if (!Number.isNaN(initialH) && initialH > 0) {
        this.style.minHeight = `${initialH}px`;
        this.#initialMinHeightSet = true;
      }

      this.#init();

      requestAnimationFrame(() => this.flickity && this.flickity.resize());
      setTimeout(() => this.flickity && (this.flickity.resize(), this.#updateProgress()), 200);
      setTimeout(() => this.flickity && (this.flickity.resize(), this.#updateProgress()), 800);
      this.#watchImages();
    }

    #cleanup() {
      this.#abort.abort();
      if (this.#ro) this.#ro.disconnect();
      if (this.#mo) this.#mo.disconnect();
      if (this.#io) this.#io.disconnect();
      this.#ro = this.#mo = this.#io = null;

      if (this.flickity) {
        this.flickity.destroy();
        this.flickity = null;
      }
      this.isInitialized = false;
    }

    #waitDOMReady() {
      if (document.readyState === "complete" || document.readyState === "interactive")
        return Promise.resolve();
      return new Promise((resolve) =>
        document.addEventListener("DOMContentLoaded", resolve, { once: true })
      );
    }

    #init() {
      if (this.isInitialized) return;

      const slides = this.querySelectorAll(".carousel__slide");
      if (!slides.length) return;

      const isMobile = window.innerWidth < 768;
      const is4_8Products = this.closest('.collection-products--4-8') !== null;
      
      if (isMobile && is4_8Products) {
        this.classList.add('mobile-grid-mode');
        this.classList.remove('carousel', 'row', 'small-up-2', 'medium-up-4');
        this.classList.add('grid', 'product-grid', 'grid--2-col-tablet-down', 'grid--4-col-desktop');
        
        // Restructure: flatten carousel slides into grid items
        const allProducts = [];
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.originalHTML;
        tempDiv.querySelectorAll(".carousel__slide .slide-product").forEach(product => {
          allProducts.push(product.cloneNode(true));
        });
        
        this.innerHTML = '';
        allProducts.forEach((product) => {
          const gridItem = document.createElement('li');
          gridItem.className = 'grid__item';
          while (product.firstChild) {
            gridItem.appendChild(product.firstChild);
          }
          this.appendChild(gridItem);
        });
        return;
      }

      const config = this.#config();
      this.flickity = new Flickity(this, config);
      this.isInitialized = true;

      this.#bindEvents();
      this.#bindObservers();

      this.#updateProgress();
      this.#updateScrolledState();
    }

    #config() {
      const columnsMatch = this.className.match(/medium-up-(\d+)/);
      const columnCount = columnsMatch ? parseInt(columnsMatch[1], 10) : 4;
      const isMobile = window.innerWidth < 768;
      const groupCells = isMobile ? 1 : columnCount;

      return {
        cellAlign: "left",
        contain: false,
        pageDots: false,
        prevNextButtons: false,
        adaptiveHeight: false,
        groupCells,
        dragThreshold: 10,
        selectedAttraction: 0.025,
        friction: 0.28,
        freeScroll: false,
        wrapAround: false,
        autoPlay: false,
        accessibility: true,
        setGallerySize: true,
        resize: true,
        percentPosition: true,
      };
    }

    #bindEvents() {
      if (this.flickity) {
        this.flickity.on("change", () => {
          this.#updateProgress();
          this.#updateScrolledState();
        });
      }

      const onResize = () => {
        if (this.#resizeScheduled) return;
        this.#resizeScheduled = true;
        requestAnimationFrame(() => {
          this.#resizeScheduled = false;
          
          const isMobile = window.innerWidth < 768;
          const is4_8Products = this.closest('.collection-products--4-8') !== null;
          const isMobileGridMode = this.classList.contains('mobile-grid-mode');
          
          if (isMobile && is4_8Products && !isMobileGridMode && this.flickity) {
            this.flickity.destroy();
            this.flickity = null;
            this.isInitialized = false;
            this.#init();
            } else if (!isMobile && isMobileGridMode) {
              this.classList.remove('mobile-grid-mode', 'grid', 'product-grid', 'grid--2-col-tablet-down', 'grid--4-col-desktop');
              this.classList.add('carousel', 'row', 'small-up-2', 'medium-up-4');
              if (this.originalHTML) {
                this.innerHTML = this.originalHTML;
              }
              this.isInitialized = false;
              this.#init();
            } else if (this.flickity) {
            this.flickity.resize();
            this.#updateProgress();
          }
        });
      };
      window.addEventListener("resize", onResize, { signal: this.#abort.signal });
    }

    #bindObservers() {
      if ("ResizeObserver" in window) {
        this.#ro = new ResizeObserver(() => {
          if (this.flickity && !this.classList.contains('mobile-grid-mode')) {
            this.flickity.resize();
            this.#updateProgress();
          }
        });
        this.#ro.observe(this);
      }

      this.#mo = new MutationObserver((mutations) => {
        if (!mutations.some((m) => m.addedNodes && m.addedNodes.length)) return;
        this.reloadCells(); // lekka operacja + nasłuch obrazków już to dociągnie
      });
      this.#mo.observe(this, { childList: true, subtree: true });
    }

    #watchImages() {
      const imgs = Array.from(this.querySelectorAll("img"));
      this.#pendingImgCount = 0;

      const scheduleRefresh = (() => {
        let scheduled = false;
        return () => {
          if (scheduled) return;
          scheduled = true;
          requestAnimationFrame(() => {
            scheduled = false;
            if (this.flickity && !this.classList.contains('mobile-grid-mode')) {
              this.flickity.reloadCells();
              this.flickity.resize();
              this.#updateProgress();
            }
            if (this.#initialMinHeightSet && this.#allImagesComplete()) {
              this.style.minHeight = "";
              this.#initialMinHeightSet = false;
            }
          });
        };
      })();

      imgs.forEach((img) => {
        if (img.complete && img.naturalWidth > 0) return;

        this.#pendingImgCount++;

        const done = () => {
          this.#pendingImgCount = Math.max(0, this.#pendingImgCount - 1);
          scheduleRefresh();
        };

        if (typeof img.decode === "function") {
          img.decode().then(done).catch(done);
        } else {
          img.addEventListener("load", done, { once: true, signal: this.#abort.signal });
          img.addEventListener("error", done, { once: true, signal: this.#abort.signal });
        }
      });

      if (this.#pendingImgCount === 0) {
        scheduleRefresh();
      }
    }

    #allImagesComplete() {
      const imgs = this.querySelectorAll("img");
      for (const img of imgs) {
        if (!(img.complete && img.naturalWidth > 0)) return false;
      }
      return true;
    }

    #updateProgress() {
      const bar = this.querySelector(".flickity-progress--bar");
      if (!bar || !this.flickity || this.flickity.slides.length <= 1) return;

      const progress =
        this.flickity.selectedIndex / (this.flickity.slides.length - 1);

      bar.style.transformOrigin = "left center";
      bar.style.willChange = "transform";
      bar.style.transform = `scaleX(${Math.max(0, Math.min(1, progress))})`;
    }

    #updateScrolledState() {
      if (!this.flickity || this.classList.contains('mobile-grid-mode')) return;
      if (this.flickity.selectedIndex > 0) {
        this.classList.add('is-scrolled');
      } else {
        this.classList.remove('is-scrolled');
      }
    }

    reloadCells() {
      if (!this.flickity || this.classList.contains('mobile-grid-mode')) return;
      this.flickity.reloadCells();
      this.flickity.resize();
      this.#updateProgress();
    }
    selectSlide(index, isWrapped = false, isInstant = false) {
      if (!this.flickity || this.classList.contains('mobile-grid-mode')) return;
      this.flickity.select(index, isWrapped, isInstant);
    }
    insertSlides(slides) {
      if (!this.flickity || !slides || this.classList.contains('mobile-grid-mode')) return;
      this.flickity.insert(slides);
      this.flickity.resize();
      this.#updateProgress();
    }
    resize() {
      if (!this.flickity || this.classList.contains('mobile-grid-mode')) return;
      this.flickity.resize();
      this.#updateProgress();
    }

    #abort;
    #ro;
    #mo;
    #io;
    #resizeScheduled;
    #pendingImgCount;
    #initialMinHeightSet;
  }

  customElements.define("slide-show", SlideShow);
}